import { Edit } from "@refinedev/mui";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { IResourceComponentsProps } from "@refinedev/core";
import { useEffect, useState } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { NumericFormatCustom } from "components/inputs/custom-numeric-input";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { UpdateGrantVestmentConfirmationModal } from "components/modal/update-grant-vestment-confirmation";
import { CategoryInfiniteSelect } from "components/inputs/paginated-drop-down";
import { TagsInfiniteSelect } from "components/inputs/multiple-choice-paginated-drop-down";
import { ImageUploadWithCrop } from "components/form/ImageUploadWithCrop";

export interface UpdatedValues {
  deadline: {
    prev: Date | undefined;
    current: Date | undefined;
  };
  goalAmount: {
    prev: number;
    current: number;
  };
  status: {
    prev: string;
    current: string;
  };
  allOrNothing: {
    prev: boolean | undefined;
    current: boolean | undefined;
  };
  successfully: boolean | undefined;
}

export const GrantVestmentUpdate: React.FC<IResourceComponentsProps> = () => {
  const {
    refineCore: { formLoading, onFinish, queryResult },
    register,
    handleSubmit,
    setValue,
    clearErrors,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    refineCoreProps: {
      redirect: "show",
      errorNotification: (error): any => {
        if (
          error?.message &&
          error?.message.includes("The Grantvestment has pending requests")
        ) {
          return {
            message: error?.message && "The Grantvestment has pending requests",
            type: "error",
          };
        } else if (
          error?.message &&
          error?.message.includes("The Grantvestment has approved requests")
        ) {
          return {
            message:
              error?.message &&
              "The Grantvestment has approved requests that need to be processed",
            type: "error",
          };
        } else
          return {
            message:
              error?.message && "Oops! something went wrong, please try later.",
            type: "error",
          };
      },
    },
    warnWhenUnsavedChanges: true,
  });

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [dataRoom, setDataRoom] = useState<{ title: string; url: string }[]>([
    { title: "", url: "" },
  ]);
  const [updates, setUpdates] = useState<{ title: string; url: string }[]>([
    { title: "", url: "" },
  ]);
  const [open, setOpen] = useState(false);
  const [aboutPreview, setAboutPreview] = useState<string>("");
  const [changedValues, setChangedValues] = useState<UpdatedValues>({
    deadline: { prev: undefined, current: undefined },
    goalAmount: {
      prev: 0,
      current: 0,
    },
    status: { prev: "", current: "" },
    allOrNothing: { prev: undefined, current: undefined },
    successfully: undefined,
  });

  const grantVestmentData = queryResult?.data?.data;

  useEffect(() => {
    setValue("about", grantVestmentData?.about);
    const aboutContent = grantVestmentData?.about || "";
    setAboutPreview(aboutContent);
    setValue("deadline", grantVestmentData?.deadline.substring(0, 10));
    setValue("sendClosingEmails", false);
    setValue("nonDAF", grantVestmentData?.nonDAF || 0);
    let currentTags: number[] = [];
    grantVestmentData?.tags.length > 0 &&
      grantVestmentData?.tags?.map((tag: any) => currentTags.push(tag.tagId));

    if (grantVestmentData?.dataRoom.length > 0) {
      let dataRoomTab: { title: string; url: string }[] = [];
      grantVestmentData?.dataRoom.map(
        (dataRoom: { title: string; url: string }) =>
          dataRoomTab.push({ title: dataRoom.title, url: dataRoom.url })
      );
      setDataRoom(dataRoomTab);
    }

    if (grantVestmentData?.updates.length > 0) {
      let updatesTab: { title: string; url: string }[] = [];
      grantVestmentData?.updates.map(
        (updates: { title: string; url: string }) =>
          updatesTab.push({ title: updates.title, url: updates.url })
      );
      setUpdates(updatesTab);
      setValue("image", null);
    }
  }, [
    grantVestmentData?.about,
    grantVestmentData?.dataRoom,
    grantVestmentData?.deadline,
    grantVestmentData?.nonDAF,
    grantVestmentData?.tags,
    grantVestmentData?.updates,
    setValue,
  ]);

  const handleDataRoom = (event: any, index: any) => {
    const { name, value } = event.target;
    let onChangeValue = [...dataRoom];
    // @ts-ignore
    onChangeValue[index][name] = value;
    setDataRoom(onChangeValue);
  };

  const handleUpdates = (event: any, index: any) => {
    const { name, value } = event.target;
    let onChangeValue = [...updates];
    // @ts-ignore
    onChangeValue[index][name] = value;
    setUpdates(onChangeValue);
  };

  return (
    <>
      <style>
        {`

        `}
      </style>
      <Edit
        title={
          <Typography variant="body1" fontWeight="bold">
            Update GrantVestment
          </Typography>
        }
        isLoading={formLoading}
        saveButtonProps={{
          onClick: (e: React.BaseSyntheticEvent) => {
            setValue("dataRoom[]", dataRoom);
            setValue("updates[]", updates);
            setValue("tagIds[]", selectedTags);
            const value = parseFloat(
              getValues("goalAmount").toString().replaceAll(",", "")
            );
            setValue("goalAmount", value);
            const fee = parseFloat(
              getValues("perAnnumFee").toString().replaceAll(",", "")
            );
            setValue("perAnnumFee", fee);
            const moicValue = parseFloat(
              getValues("moicValue").toString().replaceAll(",", "")
            );
            setValue("moicValue", moicValue);
            const nonDAF = parseFloat(
              getValues("nonDAF").toString().replaceAll(",", "")
            );
            setValue("nonDAF", nonDAF);
            handleSubmit(() => false)(e);
            if (isValid) {
              setChangedValues({
                deadline: {
                  prev: grantVestmentData?.deadline,
                  current: getValues("deadline"),
                },
                goalAmount: {
                  prev: grantVestmentData?.goalAmount,
                  current: getValues("goalAmount"),
                },
                status: {
                  prev: grantVestmentData?.status,
                  current: getValues("status"),
                },
                allOrNothing: {
                  prev: grantVestmentData?.allOrNothing,
                  current: getValues("allOrNothing"),
                },
                successfully:
                  grantVestmentData?.raisedAmount >=
                  grantVestmentData?.goalAmount,
              });

              setOpen(true);
            }
          },
        }}
      >
        {formLoading || queryResult?.status === "loading" ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box
            component="form"
            sx={{ display: "flex", flexDirection: "column" }}
            autoComplete="off"
          >
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Name
            </InputLabel>
            <TextField
              {...register("name", {
                required: "This GrantVestment name is required",
              })}
              error={!!(errors as any)?.name}
              helperText={(errors as any)?.name?.message}
              margin="normal"
              fullWidth
              style={{ marginTop: "1px" }}
              InputLabelProps={{ hidden: true }}
              type="text"
              name="name"
              defaultValue={grantVestmentData?.name}
            />
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Description
            </InputLabel>
            <TextField
              {...register("description", {
                required: "This GrantVestment description is required",
              })}
              error={!!(errors as any)?.description}
              helperText={(errors as any)?.description?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              type="textarea"
              minRows={3}
              rows={2}
              maxRows={4}
              multiline
              name="description"
              defaultValue={grantVestmentData?.description}
            />
            <div>
              <InputLabel
                style={{
                  fontSize: "12px",
                  marginBottom: "8px",
                  marginTop: "16px",
                  display: "block",
                }}
              >
                About
              </InputLabel>
              <TextField
                {...register("about", {
                  required: "This GrantVestment about is required",
                })}
                error={!!(errors as any)?.about}
                style={{ display: "none" }}
              />
              <Grid container spacing={2}>
                <Grid xs={6} md={6}>
                  <div
                    style={{
                      border: "1px solid ",
                      borderRadius: "5px 0 0 5px",
                      // borderColor: (errors as any)?.about
                      //   ? "#d3302f"
                      //   : "#e0e0e0",
                      borderColor: "#e0e0e0",
                      height: "350px",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      marginLeft:"17px",
                      marginTop:"14px"
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          overflow: "auto",
                        }}
                      >
                        <CKEditor
                          editor={ClassicEditor}
                          data={getValues("about") || ""}
                          config={{
                            placeholder: "Please enter about",
                            removePlugins: ["PoweredBy"],
                            toolbar: [
                              "heading",
                              "|",
                              "bold",
                              "italic",
                              "|",
                              "bulletedList",
                              "numberedList",
                              "|",
                              "blockQuote",
                              "insertTable",
                              "|",
                              "undo",
                              "redo",
                            ],
                          }}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            setAboutPreview(data);
                            clearErrors("about");
                            setValue("about", data, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                          onBlur={(event, editor) => {
                            const data = editor.getData();
                            setValue("about", data, {
                              shouldValidate: true,
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Grid>
                <Grid xs={6} md={6}>
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "1px",
                      height: "350px",
                      overflow: "auto",
                      padding: "16px",
                      backgroundColor: "#fafafa",
                      marginTop:"14px",
                      fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
                    }}
                  >
                    <InputLabel
                      style={{
                        fontSize: "12px",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
                      }}
                    >
                      Preview
                    </InputLabel>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: aboutPreview || getValues("about") || "",
                      }}
                      style={{
                        lineHeight: 1.6,
                        color: "#333",
                      }}
                      className="ck-content"
                    />
                  </div>
                </Grid>
              </Grid>
              <InputLabel
                style={{
                  fontSize: "12px",
                  margin: "3px",
                  color: "#d3302f",
                  paddingLeft: "10px",
                }}
              >
                {!!(errors as any)?.about && (errors as any)?.about?.message}
              </InputLabel>
            </div>
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Project By
            </InputLabel>
            <TextField
              {...register("projectBy", {
                required: "This GrantVestment project By is required",
              })}
              error={!!(errors as any)?.projectBy}
              helperText={(errors as any)?.projectBy?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              type="text"
              name="projectBy"
              defaultValue={grantVestmentData?.projectBy}
            />{" "}
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Deadline
            </InputLabel>
            <TextField
              {...register("deadline", {
                required: "This GrantVestment deadline is required",
              })}
              error={!!(errors as any)?.deadline}
              helperText={(errors as any)?.deadline?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              type="date"
              onChange={(e) => {
                setValue("deadline", e.target.value);
              }}
              name="Deadline"
            />
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Goal Amount
            </InputLabel>
            <TextField
              {...register("goalAmount", {
                required: "This GrantVestment goal amount is required",
              })}
              error={!!(errors as any)?.goalAmount}
              helperText={(errors as any)?.goalAmount?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                inputComponent: NumericFormatCustom as any,
              }}
              type="text"
              defaultValue={grantVestmentData?.goalAmount}
              name="goalAmount"
            />
            <InputLabel style={{ fontSize: "12px" }}>Non DAF</InputLabel>
            <TextField
              {...register("nonDAF", {
                validate: (value) => {
                  if (value === 0 || value === "0") return true;
                  if (!value && value !== 0) return "This non Daf is required";
                  return true;
                },
              })}
              error={!!(errors as any)?.nonDAF}
              helperText={(errors as any)?.nonDAF?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                inputComponent: NumericFormatCustom as any,
              }}
              value={getValues("nonDAF") ?? 0}
              name="nonDAF"
              style={{ marginTop: "1px" }}
            />
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              UpFront Fee
            </InputLabel>
            <TextField
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
              }}
              name="upFrontFee"
              value={0}
              style={{ marginTop: "1px" }}
              disabled
            />
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              UpFront Percentage Fee
            </InputLabel>
            <TextField
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
              }}
              name="upFrontPercentageFee"
              value={0}
              style={{ marginTop: "1px" }}
              disabled
            />
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Per Annum Fee
            </InputLabel>
            <TextField
              {...register("perAnnumFee", {
                required: "This GrantVestment per annum fee is required",
              })}
              error={!!(errors as any)?.perAnnumFee}
              helperText={(errors as any)?.perAnnumFee?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
              }}
              type="text"
              defaultValue={grantVestmentData?.perAnnumFee}
              name="perAnnumFee"
            />
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Moic value
            </InputLabel>
            <TextField
              {...register("moicValue", {
                required: "This GrantVestment moic value is required",
              })}
              error={!!(errors as any)?.moicValue}
              helperText={(errors as any)?.moicValue?.message}
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
              }}
              type="text"
              defaultValue={grantVestmentData?.moicValue}
              name="moicValue"
            />
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              All Or Nothing{" "}
            </InputLabel>
            <TextField
              {...register("allOrNothing", {})}
              error={!!(errors as any)?.allOrNothing}
              helperText={(errors as any)?.allOrNothing?.message}
              select
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              name="allOrNothing"
              defaultValue={grantVestmentData?.allOrNothing}
            >
              {[
                {
                  value: "true",
                  label: "True",
                },
                {
                  value: "false",
                  label: "False",
                },
              ].map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Display on Public Site{" "}
            </InputLabel>
            <TextField
              {...register("showGrantvestment", {})}
              error={!!(errors as any)?.showGrantvestment}
              helperText={(errors as any)?.showGrantvestment?.message}
              select
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              name="showGrantvestment"
              defaultValue={grantVestmentData?.showGrantvestment}
            >
              {[
                {
                  value: "true",
                  label: "True",
                },
                {
                  value: "false",
                  label: "False",
                },
              ].map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Status
            </InputLabel>
            <TextField
              {...register("status", {
                required: "This GrantVestment status is required",
              })}
              error={!!(errors as any)?.status}
              helperText={(errors as any)?.status?.message}
              select
              margin="normal"
              fullWidth
              InputLabelProps={{ hidden: true }}
              style={{ marginTop: "1px" }}
              name="status"
              defaultValue={grantVestmentData?.status}
            >
              {[
                {
                  value: "RAISING",
                  label: "Raising",
                },
                {
                  value: "CLOSED",
                  label: "Closed",
                },
                {
                  value: "DRAFT",
                  label: "Draft",
                },
                {
                  value: "PROMOTING",
                  label: "Promoting",
                },
                {
                  value: "CANCELED",
                  label: "Canceled",
                },
                {
                  value: "EXITED",
                  label: "Exited",
                },
              ].map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={
                    (grantVestmentData?.status === "RAISING" &&
                      (option.value === "DRAFT" ||
                        option.value === "EXITED")) ||
                    grantVestmentData?.status === option.value
                  }
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Category
            </InputLabel>
            <CategoryInfiniteSelect
              value={getValues("categoryId")}
              onChange={(categoryId) => {
                setValue("categoryId", categoryId, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                clearErrors("categoryId");
              }}
              error={!!(errors as any)?.categoryId}
              helperText={(errors as any)?.categoryId?.message}
              initialCategory={grantVestmentData?.category}
            />
            <InputLabel style={{ fontSize: "12px", padding: 2 }}>
              Image
            </InputLabel>
            <ImageUploadWithCrop
              onImageUpload={(imageUrl) => {
                setValue("imageUrl", imageUrl);
              }}
              existingImageUrl={getValues("imageUrl")}
              aspectRatio={3 / 2}
              minWidth={900}
              minHeight={600}
              recommendedWidth={1200}
              recommendedHeight={800}
              minCropWidth={600}
              minCropHeight={400}
            />
            <InputLabel sx={{ my: 3 }}>
              Optional GrantVestment attributes
            </InputLabel>
            <InputLabel
              style={{
                fontSize: "12px",
              }}
            >
              Tags
            </InputLabel>
            <TagsInfiniteSelect
              value={selectedTags}
              onChange={(tagIds) => {
                setSelectedTags(tagIds);
                setValue("tagIds[]", tagIds);
              }}
              error={!!(errors as any)?.["tagIds[]"]}
              helperText={(errors as any)?.["tagIds[]"]?.message}
              initialTags={grantVestmentData?.tags}
            />
            <InputLabel sx={{ mt: 2 }}>Data room</InputLabel>
            {dataRoom.map((item, index) => (
              <Grid container key={index}>
                <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
                  <TextField
                    id="dataRoom-title"
                    value={item.title}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Title"
                    name="title"
                    onChange={(event) => handleDataRoom(event, index)}
                  />
                </Grid>
                <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
                  <TextField
                    id="dataRoom-url"
                    value={item.url}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="URL"
                    name="url"
                    onChange={(event) => handleDataRoom(event, index)}
                  />
                </Grid>
                <Grid xs={12} sm={12} md={2} paddingRight={{ md: 2 }}>
                  {dataRoom.length > 1 && (
                    <Button
                      sx={{ my: 3 }}
                      onClick={(e) => {
                        e.preventDefault();
                        const newArray = [...dataRoom];
                        newArray.splice(index, 1);
                        setDataRoom(newArray);
                      }}
                    >
                      <CancelIcon />
                    </Button>
                  )}
                  {index === dataRoom.length - 1 && (
                    <Button
                      sx={{ my: 3 }}
                      onClick={() =>
                        setDataRoom([...dataRoom, { title: "", url: "" }])
                      }
                    >
                      {" "}
                      <AddCircleOutlineIcon />
                    </Button>
                  )}
                </Grid>
              </Grid>
            ))}
            <InputLabel>Updates</InputLabel>
            {updates.map((item, index) => (
              <Grid container key={index}>
                <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
                  <TextField
                    id="update-title"
                    value={item.title}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Title"
                    name="title"
                    onChange={(event) => handleUpdates(event, index)}
                  />
                </Grid>
                <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
                  <TextField
                    id="update-url"
                    value={item.url}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="URL"
                    name="url"
                    onChange={(event) => handleUpdates(event, index)}
                  />
                </Grid>
                <Grid xs={12} sm={12} md={2} paddingRight={{ md: 2 }}>
                  {updates.length > 1 && (
                    <Button
                      sx={{ my: 3 }}
                      onClick={(e) => {
                        e.preventDefault();
                        const newArray = [...updates];
                        newArray.splice(index, 1);
                        setUpdates(newArray);
                      }}
                    >
                      <CancelIcon />
                    </Button>
                  )}
                  {index === updates.length - 1 && (
                    <Button
                      sx={{ my: 3 }}
                      onClick={() =>
                        setUpdates([...updates, { title: "", url: "" }])
                      }
                    >
                      <AddCircleOutlineIcon />
                    </Button>
                  )}
                </Grid>
              </Grid>
            ))}
          </Box>
        )}
      </Edit>
      <UpdateGrantVestmentConfirmationModal
        visible={open}
        close={setOpen}
        handleSubmit={handleSubmit}
        onFinish={onFinish}
        UpdatedValues={changedValues!}
        grantVestmentId={grantVestmentData?.grantVestmentId || 0}
        setValue={setValue}
      />
    </>
  );
};
